<template>
  <div>
    <b-table
      :items="items"
      :fields="fields"
      :busy="isLoading"
      bordered
      hover
      class="table-hover"
    >
      <template v-slot:table-busy>
        <vcl-table
          :speed="5"
          :animate="true"
          :columns="10"
        />
      </template>
      <template #cell(code)="row">
        <div>
          <div
            class="font-weight-bold text-primary"
            style="font-size: 10px"
          >
            {{ row.item.code }}
          </div>
          <div
            class="small text-muted"
            v-if="row.item.warranty?.code"
          >
            ID BH:
            <span
              class="text-primary font-weight-bold hover-link"
              @click.stop="goWarranty(row.item.warrantyId)"
            >
              {{ row.item.warrantyId }}
            </span>
          </div>
        </div>
      </template>
      <template #cell(imeiCode)="row">
        <span
          v-b-tooltip.hover.top
          title="Tên sản phẩm"
          >{{ row.item.productName }}</span
        ><br />
        <span class="text-primary font-italic small">IMEI: {{ row.item.imeiCode }}</span>
      </template>
      <template #cell(customer)="row">
        <span class="font-weight-bold">{{ customerName(row.item) }}</span>
        <br />
        <span class="small">{{ customerPhone(row.item) }}</span>
      </template>
      <template #cell(providerName)="row">
        {{ row.item.providerName }}
      </template>
      <template #cell(info)="row">
        <div class="mb-1">
          <span class="font-weight-bold">Ngày gửi: </span>
          <span>{{ convertToDateSlash(row.item.sentDate, DATE_FORMAT) }}</span>
        </div>
        <div class="mb-2">
          <span class="font-weight-bold">Hẹn trả: </span>
          <span>{{ convertToDateSlash(row.item.expectedReceiveDate, DATE_FORMAT) }}</span>
        </div>
        <div v-if="row.item.actualReceiveDate" class="mb-2">
          <span class="font-weight-bold">Ngày nhận: </span>
          <span>{{ convertToDateSlash(row.item.actualReceiveDate, DATE_FORMAT) }}</span>
        </div>
        <div class="mb-2">
          <span class="font-weight-bold">Người lập phiếu: </span>
          <span class="text-primary font-weight-bold">{{ row.item.sentByEmployeeName }}</span>
        </div>
        <div class="mb-2">
          <span class="font-weight-bold">Trạng thái: </span>
          <OutsourceStatusBadge
            :status="row.item.status"
            :status-name="row.item.statusName"
          />
        </div>
      </template>
      <template #cell(note)="row">
        <p
          v-if="row.item.note"
          v-b-tooltip.hover
          :title="row.item.note"
        >
          {{ truncateText(row.item.note, 30) }}
        </p>
      </template>
      <template #cell(cost)="row">
        <div>
          <span
            v-b-tooltip.hover.top
            title="Chi phí dự kiến"
            class="small d-block"
            >Dự kiến:
            <span class="font-weight-bold">{{ convertPrice(row.item.estimatedCost) }}</span>
          </span>
        </div>
        <div>
          <span
            v-b-tooltip.hover.top
            title="Chi phí thực tế"
            class="small d-block"
            >Thực tế:
            <span class="font-weight-bold">{{ convertPrice(row.item.actualCost) }}</span>
          </span>
        </div>
        <div v-if="row.item.diffCost">
          <span
            v-b-tooltip.hover.top
            title="Chi phí chênh lệch"
            class="small d-block"
            >Chênh lệch:
            <span class="font-weight-bold text-danger">{{ convertPrice(row.item.diffCost) }}</span>
          </span>
        </div>
      </template>

      <template #cell(actions)="row">
        <div
          class="d-flex justify-content-center"
          @click.stop
        >
          <b-dropdown
            size="sm"
            no-caret
            right
          >
            <template slot="button-content">
              <i class="flaticon2-settings"></i>
            </template>
            <b-dropdown-item @click="viewDetail(row.item)">
              <span style="color: #3f4254; font-size: 12px">
                <i
                  style="font-size: 1rem"
                  class="flaticon2-pen mr-1"
                ></i>
                Chi tiết
              </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="row.item.status === WARRANTY_OUTSOURCE_STATUS.SENT"
              @click="$emit('receive', row.item)"
            >
              <span style="color: #3f4254; font-size: 12px">
                <i
                  style="font-size: 1rem"
                  class="fas fa-truck-loading mr-1"
                ></i>
                Nhận hàng
              </span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>
    <b-row>
      <b-col>
        <p class="mt-3 text-dark font-weight-bold">
          Hiển thị {{ fromNo }} - {{ toNo }} / {{ totalItems }} bản ghi
        </p>
      </b-col>
      <b-col>
        <BPaginationNavCustom
          :number-of-pages="numberOfPage"
          :current-page="currentPage"
          :total-page="numberOfPage"
          @page-change="$emit('page-change', $event)"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { VclTable } from 'vue-content-loading'
import BPaginationNavCustom from '@/view/base/bootstrap/BPaginationNavCustom.vue'
import { convertPrice, truncateText } from '@/utils/common'
import { WARRANTY_OUTSOURCE_STATUS } from '@/utils/enum'
import OutsourceStatusBadge from './OutsourceStatusBadge.vue'
import { convertToDateSlash, DATE_FORMAT } from '@/utils/date'

export default {
  name: 'OutsourceListTable',
  components: { VclTable, BPaginationNavCustom, OutsourceStatusBadge },
  props: {
    items: { type: Array, default: () => [] },
    isLoading: { type: Boolean, default: false },
    totalItems: { type: Number, default: 0 },
    numberOfPage: { type: Number, default: 1 },
    currentPage: { type: Number, default: 1 },
    limit: { type: Number, default: 10 },
  },
  data() {
    return {
      DATE_FORMAT,
      WARRANTY_OUTSOURCE_STATUS,
      fields: [
        {
          key: 'code',
          label: 'Mã phiếu / Mã BH',
          tdClass: 'align-middle',
          thStyle: { width: '10%' },
        },
        {
          key: 'imeiCode',
          label: 'Sản phẩm / IMEI',
          tdClass: 'align-middle',
          thStyle: { width: '15%' },
        },
        { key: 'customer', label: 'Khách hàng', tdClass: 'align-middle' },
        { key: 'providerName', label: 'Nhà cung cấp', tdClass: 'align-middle' },
        { key: 'info', label: 'Thông tin', tdClass: 'align-middle', thStyle: { width: '20%' } },
        { key: 'cost', label: 'Số tiền', tdClass: 'text-left', thStyle: { width: '10%' } },
        { key: 'note', label: 'Ghi chú', tdClass: 'text-left', thStyle: { width: '10%' } },
        {
          key: 'actions',
          label: 'Thao tác',
          tdClass: 'text-center align-middle',
          thStyle: { width: '5%' },
        },
      ],
    }
  },
  computed: {
    fromNo() {
      return (this.currentPage - 1) * this.limit + 1
    },
    toNo() {
      return Math.min(this.currentPage * this.limit, this.totalItems)
    },
  },
  methods: {
    truncateText,
    convertPrice,
    convertToDateSlash,
    customerName(row) {
      return row.customerName || (row.warranty && row.warranty.customerName) || '—'
    },
    customerPhone(row) {
      return row.customerPhone || (row.warranty && row.warranty.customerPhone) || '—'
    },
    viewDetail(item) {
      this.$emit('view', item)
    },
    goWarranty(warrantyId) {
      if (!warrantyId) return
      this.$router.push({ name: 'warranty-dispatch-info', query: { id: warrantyId } })
    },
  },
}
</script>

<style scoped>
.hover-link:hover {
  cursor: pointer;
  text-decoration: underline;
}
.table th,
.table td {
  padding: 0.75rem;
  vertical-align: middle;
}
</style>
